import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useMemo } from 'react';
import { useLazyGetInvoicedWorkByEmployeeAndYearReportQuery } from 'store/api/reportingTrends';
import { InvoicedWorkByEmployeeAndYearRow, InvoicedWorkTotals } from 'types/reporting/trends';
import { t } from 'utils/getTranslations';
import { ChartReportView } from '../charts/ChartReportView';
import { EmployeePeriodCharts } from '../charts/EmployeePeriodCharts';
import { EMPLOYEE_NAME_SEARCH, employeeTrendFilters } from '../charts/trendFilters';
import { formatReportNumber, pivotToneRule, ReportTone } from '../reportCells';
import { ReportEntry } from '../reportCatalog';
import { inBaseCurrency, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { quickFiltersFrom } from '../reportFilters';

const TONE: ReportTone = 'green';

type Row = InvoicedWorkByEmployeeAndYearRow;

const REPORT_KEY = 'invoiced-work-by-employee-and-year';

function invoicedAmount(row: Row): number | null {
    return row.invoicedAmount;
}

function buildFilterConfig(): FilterConfig {
    const filters = employeeTrendFilters();
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        quickFilters: quickFiltersFrom(filters, { isActive: 'reporting.filter.is-active' }),
        searchBarSelectors: EMPLOYEE_NAME_SEARCH,
        filters,
    };
}

function renderCharts(rows: Row[], totals: InvoicedWorkTotals, currency: string | null) {
    return (
        <EmployeePeriodCharts
            tone={TONE}
            rows={rows}
            period="year"
            value={invoicedAmount}
            measure={inBaseCurrency(t('reporting.column.invoiced-amount'), currency)}
            chartTitle={t(`reporting.${REPORT_KEY}.chart`)}
            tableTitle={t(`reporting.${REPORT_KEY}.table`)}
            total={t('reporting.chart.total-amount', { value: formatReportNumber(totals.invoicedAmount) })}
        />
    );
}

export function InvoicedWorkByEmployeeAndYearReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetInvoicedWorkByEmployeeAndYearReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const currency = useReportBaseCurrency();
    return (
        <ReportLayout
            report={report}
            toneLegend={[
                { label: inBaseCurrency(t('reporting.column.invoiced-amount'), currency), rule: pivotToneRule(TONE) },
            ]}
        >
            <ChartReportView<Row, InvoicedWorkTotals>
                analyticsSurface={`report-${REPORT_KEY}`}
                data={data}
                isFetching={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
            >
                {(rows, totals) => renderCharts(rows, totals, currency)}
            </ChartReportView>
        </ReportLayout>
    );
}
